"use client";

import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import {
  hasAnyFilter,
  useCatalogFilters,
  type SortOrder,
} from "@/stores/catalog-filters";
import type {
  CategorySlug,
  ColorTag,
} from "@/lib/i18n/catalog-strings";

const CATEGORIES: { slug: CategorySlug; label: string }[] = [
  { slug: "rose", label: "Roses" },
  { slug: "garden-spray-rose", label: "Garden & Spray" },
  { slug: "hydrangea", label: "Hydrangeas" },
  { slug: "specialty", label: "Specialty" },
  { slug: "filler-green", label: "Fillers & Greens" },
];

const COLORS: ColorTag[] = [
  "red",
  "pink",
  "hot-pink",
  "peach",
  "white",
  "cream",
  "blue",
  "green",
  "purple",
  "orange",
  "yellow",
  "lavender",
  "bicolor",
];

const LENGTHS = [50, 60, 70];

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: "az", label: "A-Z" },
  { value: "za", label: "Z-A" },
  { value: "price-asc", label: "Price low-high" },
  { value: "price-desc", label: "Price high-low" },
];

const PRICE_TIERS = [
  { value: "lt1", label: "Under $1 / stem" },
  { value: "1-2", label: "$1-$2 / stem" },
  { value: "2plus", label: "Over $2 / stem" },
];

function colorLabel(c: string): string {
  return c
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export function PowerBuyerFilterBar() {
  // Step 6.7.2 / AC165: same store as Magazine view's CategoryTabs +
  // FilterPanel. Toggling a category here flips the same chip in
  // Magazine view (and vice versa) when the user switches views.
  const categories = useCatalogFilters((s) => s.categories);
  const colors = useCatalogFilters((s) => s.colors);
  const lengths = useCatalogFilters((s) => s.lengths);
  const price = useCatalogFilters((s) => s.price);
  const sort = useCatalogFilters((s) => s.sort);
  const toggleCategory = useCatalogFilters((s) => s.toggleCategory);
  const toggleColor = useCatalogFilters((s) => s.toggleColor);
  const toggleLength = useCatalogFilters((s) => s.toggleLength);
  const setPrice = useCatalogFilters((s) => s.setPrice);
  const setSort = useCatalogFilters((s) => s.setSort);
  const clearAll = useCatalogFilters((s) => s.clearAll);

  const anyFilter = hasAnyFilter({
    categories,
    colors,
    lengths,
    price,
    sort,
  });

  return (
    <div
      data-testid="power-buyer-filter-bar"
      className="flex flex-wrap items-center gap-2 border-b border-brand-border bg-brand-surface px-4 py-3 text-[12px] md:px-5"
    >
      <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-brand-text-muted">
        Category:
      </span>
      <button
        type="button"
        data-testid="filter-category-all"
        onClick={() => {
          if (categories.length === 0) return;
          categories.forEach((c) => toggleCategory(c));
        }}
        aria-pressed={categories.length === 0}
        className={
          categories.length === 0
            ? "cursor-pointer rounded-md border border-brand-text bg-brand-text px-3.5 py-1.5 text-[12px] font-bold text-white transition-all duration-150 hover:border-brand-teal-dark hover:bg-brand-teal-dark"
            : "cursor-pointer rounded-md border border-brand-border bg-white px-3.5 py-1.5 text-[12px] font-semibold text-brand-text-muted transition-all duration-150 hover:border-brand-teal hover:bg-brand-teal-soft hover:text-brand-teal-dark"
        }
      >
        All
      </button>
      {CATEGORIES.map((c) => {
        const isActive = categories.includes(c.slug);
        return (
          <button
            key={c.slug}
            type="button"
            data-testid={`filter-category-pill-${c.slug}`}
            aria-pressed={isActive}
            onClick={() => toggleCategory(c.slug)}
            className={
              isActive
                ? "cursor-pointer rounded-md border border-brand-text bg-brand-text px-3.5 py-1.5 text-[12px] font-bold text-white transition-all duration-150 hover:border-brand-teal-dark hover:bg-brand-teal-dark"
                : "cursor-pointer rounded-md border border-brand-border bg-white px-3.5 py-1.5 text-[12px] font-semibold text-brand-text-muted transition-all duration-150 hover:border-brand-teal hover:bg-brand-teal-soft hover:text-brand-teal-dark"
            }
          >
            {c.label}
          </button>
        );
      })}

      <div className="mx-1 h-5 w-px bg-brand-border" aria-hidden="true" />

      <FilterDropdown
        testId="filter-dropdown-color"
        label={`Color: ${colors.length === 0 ? "All" : colors.length === 1 ? colorLabel(colors[0]) : `${colors.length}`}`}
        renderChildren={() =>
          COLORS.map((c) => (
            <label
              key={c}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-brand-surface"
            >
              <input
                type="checkbox"
                data-testid={`filter-dd-color-${c}`}
                checked={colors.includes(c)}
                onChange={() => toggleColor(c)}
                className="cursor-pointer accent-brand-teal"
              />
              <span>{colorLabel(c)}</span>
            </label>
          ))
        }
      />

      <FilterDropdown
        testId="filter-dropdown-length"
        label={`Length: ${lengths.length === 0 ? "All" : lengths.map((l) => `${l}cm`).join(", ")}`}
        renderChildren={() =>
          LENGTHS.map((l) => (
            <label
              key={l}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-brand-surface"
            >
              <input
                type="checkbox"
                data-testid={`filter-dd-length-${l}`}
                checked={lengths.includes(String(l))}
                onChange={() => toggleLength(String(l))}
                className="cursor-pointer accent-brand-teal"
              />
              <span>{l} cm</span>
            </label>
          ))
        }
      />

      <FilterDropdown
        testId="filter-dropdown-price"
        label={`Price: ${price ? PRICE_TIERS.find((p) => p.value === price)?.label ?? "All" : "All"}`}
        renderChildren={({ close }) => (
          <>
            <button
              type="button"
              onClick={() => {
                setPrice("");
                close();
              }}
              className="block w-full cursor-pointer px-3 py-1.5 text-left text-[12px] hover:bg-brand-surface"
            >
              All prices
            </button>
            {PRICE_TIERS.map((p) => (
              <button
                key={p.value}
                type="button"
                data-testid={`filter-dd-price-${p.value}`}
                onClick={() => {
                  setPrice(p.value);
                  close();
                }}
                className="block w-full cursor-pointer px-3 py-1.5 text-left text-[12px] hover:bg-brand-surface"
              >
                {p.label}
              </button>
            ))}
          </>
        )}
      />

      <FilterDropdown
        testId="filter-dropdown-sort"
        label={`Sort: ${SORT_OPTIONS.find((s) => s.value === sort)?.label ?? "A-Z"}`}
        renderChildren={({ close }) =>
          SORT_OPTIONS.map((s) => (
            <button
              key={s.value}
              type="button"
              data-testid={`filter-dd-sort-${s.value}`}
              onClick={() => {
                setSort(s.value);
                close();
              }}
              className="block w-full cursor-pointer px-3 py-1.5 text-left text-[12px] hover:bg-brand-surface"
            >
              {s.label}
            </button>
          ))
        }
      />

      {anyFilter && (
        <button
          type="button"
          data-testid="filter-clear-all"
          onClick={clearAll}
          className="ml-auto cursor-pointer text-[12px] font-semibold text-brand-teal-dark hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

// Step 6.7.2 / AC168 + AC169 + AC170: real Popover with outside-click
// light-dismiss, Escape close, and focus return to trigger. Single-select
// dimensions (Sort, Price) call close() inside their option handlers.
// Multi-select (Color, Length) just toggle store and rely on outside-
// click / Escape to dismiss.
function FilterDropdown({
  testId,
  label,
  renderChildren,
}: {
  testId: string;
  label: string;
  renderChildren: (api: { close: () => void }) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        data-testid={testId}
        className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-md border border-brand-border bg-white px-3 py-1.5 text-[12px] font-semibold text-brand-text hover:border-brand-teal data-[state=open]:border-brand-teal"
      >
        {label}
        <span aria-hidden="true" className="text-[9px] text-brand-text-muted">
          v
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          data-testid={`${testId}-content`}
          align="start"
          sideOffset={4}
          className="z-30 w-52 max-h-72 overflow-y-auto rounded-md border border-brand-border bg-white py-1 shadow-lg"
        >
          {renderChildren({ close: () => setOpen(false) })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
